import { getCategoryHierarchy } from '@/helpers/getCategoryHierarchy'

import type { CategoryHierarchy } from '@/gen/ecom'
import type { LocaleText } from '@/gen/sanity/schema'
import type { NavigationNode, TopNavigationNode } from '@/interfaces/NavigationNode'
import type { SanityLinkTarget, SanityMenuItem, SanityNavigationItem } from '@/interfaces/SanityHeader'
import type { TranslationLanguage } from '@/lib/translate'

const COLUMN_CATEGORY_LEVELS = 3
const PANE_CATEGORY_LEVELS = 4

export interface NavigationContext {
  catalog: CategoryHierarchy[]
  language: TranslationLanguage
  isLoggedIn: boolean
}

export const buildNavigationTree = (menuItems: SanityMenuItem[], context: NavigationContext): TopNavigationNode[] =>
  toMenuItemNodes(menuItems, context, COLUMN_CATEGORY_LEVELS)

const toMenuItemNodes = (menuItems: SanityMenuItem[], context: NavigationContext, categoryLevels: number) =>
  menuItems
    .filter((menuItem) => context.isLoggedIn || !menuItem.loggedInOnly)
    .map((menuItem) => toMenuItemNode(menuItem, context, categoryLevels))
    .filter(isNavigable)

const toMenuItemNode = (menuItem: SanityMenuItem, context: NavigationContext, categoryLevels: number) => {
  if (menuItem.subMenuItems?.length) {
    return toPanesNode(menuItem, context)
  }
  if (menuItem.treeType === 'customTree') {
    return toCustomTreeNode(menuItem, context.language)
  }

  return toCategoryNodeFromMenuItem(menuItem, context, categoryLevels)
}

const isNavigable = <Node extends NavigationNode>(node?: Node): node is Node =>
  !!node && (node.children.length > 0 || !!node.href)

const toPanesNode = (menuItem: SanityMenuItem, context: NavigationContext): TopNavigationNode => ({
  id: menuItem._id,
  title: localize(menuItem.title, context.language),
  ...toLinkColorProps(menuItem.link),
  children: toMenuItemNodes(menuItem.subMenuItems ?? [], context, PANE_CATEGORY_LEVELS),
  megaMenuLayout: 'panes',
})

const toCustomTreeNode = (menuItem: SanityMenuItem, language: TranslationLanguage): TopNavigationNode => ({
  id: menuItem._id,
  title: localize(menuItem.title, language),
  ...toLinkProps(menuItem.link),
  children: toNavigationItemNodes(menuItem.navigationItems, language),
  megaMenuLayout: 'columns',
})

const toCategoryNodeFromMenuItem = (
  menuItem: SanityMenuItem,
  context: NavigationContext,
  categoryLevels: number
): TopNavigationNode | undefined => {
  const category = getCategoryHierarchy(context.catalog, menuItem.mainCategoryId, categoryLevels)
  if (category?.hideFromNavigation) {
    return undefined
  }

  return {
    id: menuItem._id,
    title: localize(menuItem.title, context.language) || category?.name || '',
    href: category?.url ?? getCategoryPageUrl(menuItem),
    children: category ? toVisibleSubcategoryNodes(category) : [],
    megaMenuLayout: 'columns',
  }
}

const toNavigationItemNodes = (
  navigationItems: SanityNavigationItem[] | null | undefined,
  language: TranslationLanguage
): NavigationNode[] =>
  (navigationItems ?? [])
    .map((navigationItem) => ({
      id: navigationItem._id,
      title: localize(navigationItem.title, language),
      ...toLinkProps(navigationItem.target),
      children: toNavigationItemNodes(navigationItem.navigationItems, language),
    }))
    .filter(isNavigable)

const toCategoryNode = (category: CategoryHierarchy): NavigationNode => ({
  id: category.id ?? '',
  title: category.name ?? '',
  href: category.url,
  children: toVisibleSubcategoryNodes(category),
})

const toVisibleSubcategoryNodes = (category: CategoryHierarchy) =>
  (category.subcategories ?? []).filter(isVisibleCategory).map(toCategoryNode)

const isVisibleCategory = (category?: CategoryHierarchy): category is CategoryHierarchy =>
  !!category && !category.hideFromNavigation

const toLinkProps = (target?: SanityLinkTarget | null) => ({
  href: target?.href ?? undefined,
  openInNewTab: target?.openInNewTab ?? undefined,
  ...toLinkColorProps(target),
})

const toLinkColorProps = (target?: SanityLinkTarget | null) => ({
  backgroundColor: target?.backgroundColor ?? undefined,
  textColor: target?.textColor ?? undefined,
})

const getCategoryPageUrl = ({ sanityName, mainCategoryId }: SanityMenuItem) =>
  mainCategoryId ? `/${sanityName}/c/${mainCategoryId}`.toLocaleLowerCase() : undefined

const localize = (text: LocaleText | undefined, language: TranslationLanguage) => text?.[language] ?? ''
